import { VendorTaxonomyKey } from '@zola-helpers/client/dist/es/marketplace/vendorTaxonomyKeys';
import { VendorTypeEnum } from '@zola-helpers/client/dist/es/marketplace/vendorUtils';
import type {
  RelatedLinkView,
  SearchLocationView,
  SeoContentView,
  SeoSrpMetricDataView,
  StorefrontCardView,
  USCityView,
} from '@zola/svc-marketplace-ts-types';

import { isServerApiService } from '~/libs/universal/util/apiServiceType';
import type { VendorSearchRequest } from '~/pages/couples/explore/components/SearchResults/types/types';
import { pickCanonicalFacets } from '~/pages/couples/explore/util/canonicalUrl';
import type { TaxonomyNodeView, VendorCardView } from '~/types/responseTypes';
import type { ClientApiServiceType } from '~/util/apiService';
import getCanonicalUrl from '~/util/storefrontUrl';

import ServerApiService, {
  ApiServiceTarget,
  ServerApiServiceType,
} from '../server/util/apiService';

export const getExpertAdviceRelatedLinks = async (
  topicSlug: string,
  subtopicSlug: string,
  apiService: ServerApiServiceType | ClientApiServiceType = ServerApiService
): Promise<RelatedLinkView[]> => {
  const url = `/web-marketplace-api/v1/seo/related-links/topic/${topicSlug}/subtopic/${subtopicSlug}`;
  const request = isServerApiService(apiService)
    ? apiService.get<RelatedLinkView[]>(ApiServiceTarget.WEB_MARKETPLACE, url)
    : apiService.get<RelatedLinkView[]>(url);
  return request.then((relatedLinks) => relatedLinks || []).catch(() => []);
};

const mapVendorCardToRelatedLink = (vendorCard: VendorCardView): RelatedLinkView => {
  const { taxonomyNode, storefrontSlug, vendorName } = vendorCard;
  const type = (taxonomyNode as TaxonomyNodeView).key;
  const slug = storefrontSlug as string;
  const urlPath = getCanonicalUrl(type, slug);
  const title = vendorName as string;
  return {
    title,
    urlPath,
  };
};

export const getStorefrontRelatedLinks = async (
  uuid: string,
  apiService: ServerApiServiceType | ClientApiServiceType = ServerApiService
) => {
  const request = isServerApiService(apiService)
    ? apiService.get<VendorCardView[]>(
        ApiServiceTarget.SVC_MARKETPLACE,
        `/v1/storefront/${uuid}/suggested-seo?limit=12`
      )
    : apiService.get<VendorCardView[]>(
        `/web-marketplace-api/v1/storefront/${uuid}/suggested-seo?limit=12`
      );
  return request.then((vendorCards) => vendorCards.map(mapVendorCardToRelatedLink));
};

export const getStorefrontSimilarLinks = async ({
  apiService,
  limit = 12,
  storefrontUuid,
  vendorMarketId,
  vendorType,
}: {
  apiService: ServerApiServiceType | ClientApiServiceType;
  limit?: number;
  storefrontUuid: string;
  vendorMarketId: number;
  vendorType: VendorTypeEnum;
}) => {
  const body = { vendorTypes: [vendorType], vendorMarketIds: [vendorMarketId] };
  const request = isServerApiService(apiService)
    ? apiService.post<StorefrontCardView[]>(
        ApiServiceTarget.SVC_MARKETPLACE,
        `/v1/storefront-suggestions/storefront/${storefrontUuid}/similar-storefronts?limit=${limit}`,
        body
      )
    : apiService.post<StorefrontCardView[]>(
        `/web-marketplace-api/v1/storefront-suggestions/storefront/${storefrontUuid}/similar-storefronts?limit=${limit}`,
        body
      );
  return request.then((storefrontCards) =>
    storefrontCards.map((storefrontCard) => ({
      title: storefrontCard.name,
      urlPath: storefrontCard.slug,
    }))
  );
};

export const getRelatedLinksForVendorSearch = (
  vendorSearch: VendorSearchRequest,
  apiService: ServerApiServiceType | ClientApiServiceType = ServerApiService
) => {
  const { selectedLocation, selectedTaxonomy } = vendorSearch;
  const citySlug = selectedLocation?.type === 'CITY' ? selectedLocation?.slug : undefined;
  const taxonomyKey = selectedTaxonomy?.key;
  return getLocationAndTaxonomyRelatedLinks(citySlug, taxonomyKey, apiService);
};

export const getLocationAndTaxonomyRelatedLinks = async (
  citySlug: string | undefined,
  taxonomyKey: VendorTaxonomyKey | undefined,
  apiService: ServerApiServiceType | ClientApiServiceType = ServerApiService
) => {
  if (citySlug && taxonomyKey) {
    const request = isServerApiService(apiService)
      ? apiService.get<RelatedLinkView[]>(
          ApiServiceTarget.SVC_MARKETPLACE,
          `/v1/seo/related-links/city/${citySlug}/taxonomy_node/${taxonomyKey}`
        )
      : apiService.get<RelatedLinkView[]>(
          `/web-marketplace-api/v1/seo/related-links/city/${citySlug}/taxonomy_node/${taxonomyKey}`
        );
    return request.catch(() => []);
  }

  return Promise.resolve([]);
};

type SeoContentRequest = {
  vendorMarketKey: string;
  taxonomyNodeKey: string;
  appliedPrimaryFacet: string | null;
} & (
  | {
      citySlug: string;
      stateSlug: null;
    }
  | {
      citySlug: null;
      stateSlug: string;
    }
);

/**
 * Get SEO content block HTML for the search results page
 *
 * NOTE: The search location can be a state, market or city, but this will only get content for cities
 */
export const getSeoContentBlockHtml = async (
  vendorSearch: VendorSearchRequest,
  apiService: ServerApiServiceType | ClientApiServiceType = ServerApiService
): Promise<SeoContentView | null> => {
  const { selectedLocation, selectedTaxonomy } = vendorSearch;
  const { type, slug, marketSlug } = selectedLocation || {};
  const taxonomyNodeKey = selectedTaxonomy?.key;

  const primaryFacet = pickCanonicalFacets(
    taxonomyNodeKey,
    vendorSearch.selectedFacets || [],
    true
  );
  const appliedPrimaryFacet = primaryFacet.length === 1 ? primaryFacet[0].key : null;

  if (type === 'CITY' && slug && marketSlug && taxonomyNodeKey) {
    const body: SeoContentRequest = {
      citySlug: slug,
      stateSlug: null,
      vendorMarketKey: marketSlug,
      taxonomyNodeKey,
      appliedPrimaryFacet,
    };
    const request = isServerApiService(apiService)
      ? apiService.post<SeoContentView, SeoContentRequest>(
          ApiServiceTarget.SVC_MARKETPLACE,
          '/v1/seo/content',
          body
        )
      : apiService.post<SeoContentView, SeoContentRequest>(
          '/web-marketplace-api/v1/seo/content',
          body
        );
    return request.catch(() => null);
  }

  return Promise.resolve(null);
};

export const getSeoTopLocations = async (
  vendorSearch: VendorSearchRequest,
  apiService: ServerApiServiceType | ClientApiServiceType = ServerApiService
): Promise<SearchLocationView[]> => {
  const { selectedLocation } = vendorSearch;

  if (!selectedLocation || selectedLocation.type === 'NO_MARKETS') {
    return Promise.resolve([]);
  }

  const statePart =
    selectedLocation.type === 'ALL_MARKETS' ? '' : `/state/${selectedLocation.stateSlug}`;

  const request = isServerApiService(apiService)
    ? apiService.get<SearchLocationView[]>(
        ApiServiceTarget.SVC_MARKETPLACE,
        `/v1/search-location/top-locations${statePart}`
      )
    : apiService.get<SearchLocationView[]>(
        `/web-marketplace-api/v1/search-location/top-locations${statePart}`
      );

  return request.catch(() => []);
};

export const getSeoNearbyCities = async (
  vendorSearch: VendorSearchRequest,
  apiService: ServerApiServiceType | ClientApiServiceType = ServerApiService
) => {
  const { selectedLocation } = vendorSearch;

  if (!selectedLocation || selectedLocation.type !== 'CITY') {
    return Promise.resolve([]);
  }

  const request = isServerApiService(apiService)
    ? apiService.get<USCityView[]>(
        ApiServiceTarget.SVC_MARKETPLACE,
        `/v1/us-city/nearby-cities/${selectedLocation.slug}?limit=120`
      )
    : apiService.get<USCityView[]>(
        `/web-marketplace-api/v1/us-city/nearby-cities/${selectedLocation.slug}?limit=120`
      );

  return request.catch(() => []);
};

export const getSeoMetrics = async (
  vendorSearch: VendorSearchRequest,
  apiService: ServerApiServiceType | ClientApiServiceType = ServerApiService
) => {
  const { selectedLocation, selectedTaxonomy } = vendorSearch;

  if (!selectedLocation || selectedLocation.type !== 'CITY') {
    return Promise.resolve(null);
  }

  const citySlug = selectedLocation?.slug;
  const vendorTypeSlug = selectedTaxonomy?.key ? `?vendorType=${selectedTaxonomy.key}` : '';
  const request = isServerApiService(apiService)
    ? apiService.get<SeoSrpMetricDataView>(
        ApiServiceTarget.SVC_MARKETPLACE,
        `/v1/seo/seo-metrics/city/${citySlug}${vendorTypeSlug}`
      )
    : apiService.get<SeoSrpMetricDataView>(
        `/web-marketplace-api/v1/seo/seo-metrics/city/${citySlug}${vendorTypeSlug}`
      );

  return request.catch(() => null);
};
